/* Classification of an IBM purchase-method call, kept deliberately apart from the
transport. The gate must never treat a degraded dependency as a business answer, so
any outcome that is not a well-formed envelope carrying an explicit verdict is
reported as unavailable instead of guessed. */

export const PURCHASE_METHOD_RESPONSE = {
  VALID: 'valid',
  INVALID: 'invalid',
  UNAVAILABLE: 'unavailable',
} as const;

export type PurchaseMethodResponseStatus =
  typeof PURCHASE_METHOD_RESPONSE[keyof typeof PURCHASE_METHOD_RESPONSE];

/* What the caller observed after awaiting an IBM call: either the transport handed
back a parsed body, or it failed before producing one. Both non-2xx responses and
network failures arrive as 'failure', because the transport rejects on both. */
export type IbmCallOutcome =
  | { settled: 'body'; body: unknown }
  | { settled: 'failure'; error: unknown };

export interface PurchaseMethodDefaultClassification {
  status: PurchaseMethodResponseStatus;
  /* Raw IBM value ('Sin Valor' | 'C' | 'P') when the answer is usable, null otherwise.
  Turning it into a domain value stays with mapBuyerPurchaseMethodDefault. */
  rawValue: string | null;
}

export interface PurchaseMethodValidationResult {
  status: PurchaseMethodResponseStatus;
  /* IBM's own explanation for a rejection (status.messageError), verbatim, so the
  surface can show the buyer's actual problem instead of a guessed diagnosis. Null on
  approval and on an unavailable verdict, where there is nothing of IBM's to show. */
  messageError: string | null;
}

/* Matches the comparison the existing callers already perform, so classification does
not silently widen or narrow which verdicts count as approved. */
const OK_STATUS = 'ok';

const DEFAULT_VALUE_KEY = 'Valor por Defecto';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/* The envelope is { status: { status }, data: { Accion, msg } }. Only the verdict flag
is required here; a body that does not expose it cannot be interpreted at all. */
function readEnvelopeStatus(body: unknown): string | null {
  if (!isRecord(body)) {
    return null;
  }

  const statusBlock = body['status'];
  if (!isRecord(statusBlock)) {
    return null;
  }

  const status = statusBlock['status'];
  return typeof status === 'string' ? status : null;
}

function readDefaultValue(body: unknown): string | null {
  if (!isRecord(body)) {
    return null;
  }

  const data = body['data'];
  if (!isRecord(data)) {
    return null;
  }

  const msg = data['msg'];
  if (!isRecord(msg)) {
    return null;
  }

  const value = msg[DEFAULT_VALUE_KEY];
  return typeof value === 'string' ? value : null;
}

/* The envelope is { status: { status, message, messageError } }. Only read once the
verdict is already known to be a rejection: IBM leaves this null on approval. */
function readEnvelopeMessageError(body: unknown): string | null {
  if (!isRecord(body)) {
    return null;
  }

  const statusBlock = body['status'];
  if (!isRecord(statusBlock)) {
    return null;
  }

  const messageError = statusBlock['messageError'];
  return typeof messageError === 'string' ? messageError : null;
}

/* VALCOMO. Live verification against dev-be-ibm (2026-09-08, see ADR-0001) showed that a
real business rejection and a degraded dependency both answer HTTP 200 with a well-formed
envelope and `data: null` -- presence of data does not distinguish them, so it is not
used here. Any envelope that exposes an explicit non-ok verdict is treated as a real
rejection of the pair, and its messageError is carried along verbatim for the surface to
show. A failure or an unreadable envelope, where no verdict could be read at all, is
unavailable. Both outcomes block the gate, so this distinction only decides which message
the operator sees, never whether the action is allowed. */
export function classifyPurchaseMethodValidation(
  outcome: IbmCallOutcome
): PurchaseMethodValidationResult {
  if (outcome.settled === 'failure') {
    return { status: PURCHASE_METHOD_RESPONSE.UNAVAILABLE, messageError: null };
  }

  const status = readEnvelopeStatus(outcome.body);
  if (status === null) {
    return { status: PURCHASE_METHOD_RESPONSE.UNAVAILABLE, messageError: null };
  }

  if (status === OK_STATUS) {
    return { status: PURCHASE_METHOD_RESPONSE.VALID, messageError: null };
  }

  return {
    status: PURCHASE_METHOD_RESPONSE.INVALID,
    messageError: readEnvelopeMessageError(outcome.body),
  };
}

/* COMODEF. There is no such thing as an invalid buyer default, so a non-ok verdict and
an unreadable payload both mean the default could not be consulted, which must block
rather than pass as "no default". 'Sin Valor' is a successful answer that happens to
carry no default and is classified as valid. */
export function classifyPurchaseMethodDefault(
  outcome: IbmCallOutcome
): PurchaseMethodDefaultClassification {
  if (outcome.settled === 'failure') {
    return { status: PURCHASE_METHOD_RESPONSE.UNAVAILABLE, rawValue: null };
  }

  const status = readEnvelopeStatus(outcome.body);
  const rawValue = readDefaultValue(outcome.body);

  if (status !== OK_STATUS || rawValue === null) {
    return { status: PURCHASE_METHOD_RESPONSE.UNAVAILABLE, rawValue: null };
  }

  return { status: PURCHASE_METHOD_RESPONSE.VALID, rawValue };
}
